/**
 * HumanModel — Figura humana holográfica con líneas y puntos
 * Estilo medical-scan, no realista. Rotación independiente de la escena.
 */

import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { AnatomicalSystem } from '../types/medical';

// ═══════════════════════════════════════════════════════════════════════════════
// PUNTOS DE ANCLAJE ANATÓMICOS (posición local en el cuerpo)
// ═══════════════════════════════════════════════════════════════════════════════

const BODY_ANCHORS: Record<AnatomicalSystem, THREE.Vector3> = {
  cardiovascular: new THREE.Vector3(0.08, 0.45, 0.08),
  respiratory: new THREE.Vector3(-0.06, 0.55, 0.08),
  neurological: new THREE.Vector3(0, 1.15, 0),
  gastrointestinal: new THREE.Vector3(0, 0.05, 0.06),
  renal: new THREE.Vector3(0.12, 0.15, -0.02),
  metabolic: new THREE.Vector3(-0.1, 0.1, 0.05),
  infectious: new THREE.Vector3(0, 0.35, 0),
  musculoskeletal: new THREE.Vector3(0.25, -0.1, 0),
  endocrine: new THREE.Vector3(0, 0.95, 0.05),
  hematologic: new THREE.Vector3(-0.08, 0.3, 0),
};

const SYSTEM_COLORS: Record<AnatomicalSystem, number> = {
  cardiovascular: 0xEF4444,
  respiratory: 0x3B82F6,
  neurological: 0xF59E0B,
  gastrointestinal: 0x10B981,
  renal: 0x8B5CF6,
  metabolic: 0xEC4899,
  infectious: 0x14B8A6,
  musculoskeletal: 0xF97316,
  endocrine: 0x84CC16,
  hematologic: 0x06B6D4,
};

// ═══════════════════════════════════════════════════════════════════════════════
// CONTORNO DEL CUERPO HUMANO (coordenadas 2D del perfil, se extruyen)
// ═══════════════════════════════════════════════════════════════════════════════

// Silueta derecha del cuerpo (se espeja para la izquierda)
const BODY_OUTLINE_RIGHT: [number, number][] = [
  // Cabeza
  [0, 1.25], [0.08, 1.22], [0.12, 1.15], [0.13, 1.05], [0.12, 0.95],
  [0.08, 0.9], [0.04, 0.88],
  // Cuello
  [0.05, 0.85], [0.06, 0.78],
  // Hombro
  [0.08, 0.76], [0.2, 0.74], [0.28, 0.72],
  // Brazo
  [0.3, 0.68], [0.32, 0.55], [0.33, 0.4], [0.34, 0.25],
  // Antebrazo
  [0.35, 0.15], [0.36, 0.0], [0.37, -0.15],
  // Mano
  [0.38, -0.25], [0.37, -0.3],
  // Subir por dentro del brazo
  [0.32, -0.2], [0.3, -0.1], [0.28, 0.05], [0.26, 0.2],
  [0.25, 0.35], [0.24, 0.5], [0.22, 0.62],
  // Torso lateral
  [0.2, 0.65], [0.18, 0.5], [0.19, 0.35], [0.2, 0.2],
  [0.19, 0.05], [0.17, -0.05],
  // Cadera
  [0.18, -0.1], [0.19, -0.15],
  // Muslo
  [0.18, -0.2], [0.17, -0.35], [0.16, -0.5], [0.15, -0.65],
  // Rodilla
  [0.14, -0.72], [0.13, -0.75],
  // Pantorrilla
  [0.12, -0.8], [0.11, -0.95], [0.1, -1.05],
  // Pie
  [0.1, -1.1], [0.14, -1.13], [0.15, -1.15],
  // Planta
  [0.08, -1.15], [0.04, -1.14],
];

const BODY_OUTLINE_LEFT: [number, number][] = BODY_OUTLINE_RIGHT.map(([x, y]): [number, number] => [-x, y]).reverse();

// ═══════════════════════════════════════════════════════════════════════════════
// CLASE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

export class HumanModel extends THREE.Group {
  private regionHighlights: Map<string, THREE.Mesh> = new Map();
  private pulsePhase = 0;
  private activeHighlights: Set<AnatomicalSystem> = new Set();
  private connectionLines: THREE.Group;
  private anchorSprites: Map<string, THREE.Sprite> = new Map();

  // Interacción independiente
  private isDragging = false;
  private dragPrev = { x: 0, y: 0 };
  private userRotY = 0;
  private userRotX = 0;
  private autoRotSpeed = 0.15;
  private orbitControls: OrbitControls | null = null;

  constructor() {
    super();
    this.connectionLines = new THREE.Group();
    this.add(this.connectionLines);
    this.buildOutline();
    this.buildAnatomicalPoints();
    this.buildOrganRegions();
  }

  // ═══════════════════════════════════════════════════════════════════════
  // CONSTRUCCIÓN
  // ═══════════════════════════════════════════════════════════════════════

  private buildOutline(): void {
    const color = 0x2997FF;

    // Contorno principal — más brillante
    this.addLine(BODY_OUTLINE_RIGHT, color, 0.8);
    this.addLine(BODY_OUTLINE_LEFT, color, 0.8);

    // Líneas internas estructurales (clavícula, costillas, pelvis)
    const internals: [number, number][][] = [
      // Clavículas
      [[-0.2, 0.74], [0, 0.76], [0.2, 0.74]],
      // Costillas sugeridas
      [[-0.16, 0.6], [0, 0.62], [0.16, 0.6]],
      [[-0.17, 0.48], [0, 0.5], [0.17, 0.48]],
      [[-0.18, 0.36], [0, 0.38], [0.18, 0.36]],
      // Pelvis
      [[-0.18, -0.1], [0, -0.15], [0.18, -0.1]],
      // Columna
      [[0, 0.76], [0, -0.15]],
      // Piernas internas
      [[0, -0.15], [0.04, -1.14]],
      [[0, -0.15], [-0.04, -1.14]],
    ];
    internals.forEach(pts => this.addLine(pts, color, 0.25));

    // Círculo de cabeza
    this.addCircle(0, 1.08, 0.14, color, 0.6);

    // Órganos internos simplificados
    // Corazón
    this.addCircle(0.06, 0.48, 0.06, 0xEF4444, 0.5);
    this.addDot(0.06, 0.48, 0.025, 0xEF4444, 0.7);
    // Pulmones
    this.addCircle(-0.08, 0.55, 0.08, 0x3B82F6, 0.3);
    this.addCircle(0.14, 0.55, 0.08, 0x3B82F6, 0.3);
    // Cerebro
    this.addCircle(0, 1.08, 0.1, 0xF59E0B, 0.2);
    // Estómago/intestino
    this.addCircle(0, 0.08, 0.07, 0x10B981, 0.25);
    // Riñones
    this.addDot(0.12, 0.18, 0.02, 0x8B5CF6, 0.5);
    this.addDot(-0.12, 0.18, 0.02, 0x8B5CF6, 0.5);

    // Puntos en articulaciones — más grandes
    const joints: [number, number][] = [
      [0.28, 0.72], [-0.28, 0.72],
      [0.33, 0.4], [-0.33, 0.4],
      [0.37, -0.15], [-0.37, -0.15],
      [0.18, -0.1], [-0.18, -0.1],
      [0.14, -0.72], [-0.14, -0.72],
      [0.1, -1.1], [-0.1, -1.1],
    ];
    joints.forEach(([x, y]) => this.addDot(x, y, 0.018, color, 0.7));

    // Labels anatómicos
    this.addLabel(0.06, 0.48, 'Corazón', 0xEF4444);
    this.addLabel(-0.08, 0.6, 'Pulmón', 0x3B82F6);
    this.addLabel(0, 1.18, 'Cerebro', 0xF59E0B);
  }

  private buildAnatomicalPoints(): void {
    // Puntos brillantes en cada anclaje anatómico
    Object.entries(BODY_ANCHORS).forEach(([system, pos]) => {
      const color = SYSTEM_COLORS[system as AnatomicalSystem];
      const canvas = document.createElement('canvas');
      canvas.width = 32; canvas.height = 32;
      const ctx = canvas.getContext('2d')!;
      const grad = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
      const hex = '#' + color.toString(16).padStart(6, '0');
      grad.addColorStop(0, hex + 'FF');
      grad.addColorStop(0.4, hex + '80');
      grad.addColorStop(1, hex + '00');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, 32, 32);

      const tex = new THREE.CanvasTexture(canvas);
      const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
        map: tex, transparent: true, opacity: 0.7,
        blending: THREE.AdditiveBlending, depthWrite: false,
      }));
      sprite.position.copy(pos);
      sprite.scale.set(0.12, 0.12, 1);
      this.add(sprite);
      this.anchorSprites.set(system, sprite);
    });
  }

  private buildOrganRegions(): void {
    Object.entries(BODY_ANCHORS).forEach(([system, pos]) => {
      const color = SYSTEM_COLORS[system as AnatomicalSystem];
      const geo = new THREE.SphereGeometry(0.1, 12, 12);
      const mat = new THREE.MeshBasicMaterial({
        color, transparent: true, opacity: 0, depthWrite: false,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.copy(pos);
      this.add(mesh);
      this.regionHighlights.set(system, mesh);
    });
  }

  // ═══════════════════════════════════════════════════════════════════════
  // HELPERS DE GEOMETRÍA
  // ═══════════════════════════════════════════════════════════════════════

  private addLine(points: [number, number][], color: number, opacity: number): void {
    const verts = points.map(([x, y]) => new THREE.Vector3(x, y, 0));
    const geo = new THREE.BufferGeometry().setFromPoints(verts);
    const mat = new THREE.LineBasicMaterial({ color, transparent: true, opacity });
    this.add(new THREE.Line(geo, mat));
  }

  private addCircle(cx: number, cy: number, radius: number, color: number, opacity: number): void {
    const segments = 32;
    const points: THREE.Vector3[] = [];
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      points.push(new THREE.Vector3(
        cx + Math.cos(angle) * radius,
        cy + Math.sin(angle) * radius,
        0
      ));
    }
    const geo = new THREE.BufferGeometry().setFromPoints(points);
    const mat = new THREE.LineBasicMaterial({ color, transparent: true, opacity });
    this.add(new THREE.Line(geo, mat));
  }

  private addLabel(x: number, y: number, text: string, color: number): void {
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 32;
    const ctx = canvas.getContext('2d')!;
    ctx.font = '11px -apple-system, BlinkMacSystemFont, Inter, sans-serif';
    ctx.fillStyle = '#' + color.toString(16).padStart(6, '0');
    ctx.globalAlpha = 0.6;
    ctx.textAlign = 'center';
    ctx.fillText(text, 64, 20);
    const tex = new THREE.CanvasTexture(canvas);
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
      map: tex, transparent: true, depthWrite: false,
    }));
    sprite.position.set(x, y + 0.08, 0.05);
    sprite.scale.set(0.3, 0.08, 1);
    this.add(sprite);
  }

  private addDot(x: number, y: number, size: number, color: number, opacity: number): void {
    const geo = new THREE.SphereGeometry(size, 6, 6);
    const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(x, y, 0);
    this.add(mesh);
  }

  // ═══════════════════════════════════════════════════════════════════════
  // INTERACCIÓN INDEPENDIENTE
  // ═══════════════════════════════════════════════════════════════════════

  enableInteraction(camera: THREE.Camera, canvas: HTMLCanvasElement, controls?: OrbitControls): void {
    this.orbitControls = controls || null;

    // Hitbox para detectar hover/click sobre el modelo
    const hitbox = new THREE.Mesh(
      new THREE.BoxGeometry(1.0, 2.6, 0.6),
      new THREE.MeshBasicMaterial({ visible: false })
    );
    hitbox.position.set(0, 0.05, 0);
    this.add(hitbox);

    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    const isOver = (e: MouseEvent): boolean => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      return raycaster.intersectObject(hitbox).length > 0;
    };

    canvas.addEventListener('pointerdown', (e: PointerEvent) => {
      if (isOver(e)) {
        this.isDragging = true;
        this.dragPrev = { x: e.clientX, y: e.clientY };
        if (this.orbitControls) this.orbitControls.enabled = false;
        canvas.style.cursor = 'grabbing';
      }
    });

    canvas.addEventListener('pointermove', (e: PointerEvent) => {
      if (this.isDragging) {
        const dx = e.clientX - this.dragPrev.x;
        const dy = e.clientY - this.dragPrev.y;
        this.userRotY += dx * 0.01;
        this.userRotX = Math.max(-0.6, Math.min(0.6, this.userRotX + dy * 0.005));
        this.dragPrev = { x: e.clientX, y: e.clientY };
      } else if (isOver(e)) {
        canvas.style.cursor = 'grab';
      }
    });

    window.addEventListener('pointerup', () => {
      if (this.isDragging) {
        this.isDragging = false;
        if (this.orbitControls) this.orbitControls.enabled = true;
        canvas.style.cursor = 'default';
      }
    });

    canvas.addEventListener('wheel', (e: WheelEvent) => {
      if (isOver(e as unknown as MouseEvent)) {
        e.preventDefault();
        e.stopPropagation();
        const d = e.deltaY > 0 ? -0.15 : 0.15;
        const s = Math.max(1.2, Math.min(4.5, this.scale.x + d));
        this.scale.setScalar(s);
      }
    }, { passive: false });

    canvas.addEventListener('dblclick', (e: MouseEvent) => {
      if (isOver(e)) {
        this.userRotY = 0;
        this.userRotX = 0;
        this.scale.setScalar(2.2);
      }
    });
  }

  // ═══════════════════════════════════════════════════════════════════════
  // API PÚBLICA
  // ═══════════════════════════════════════════════════════════════════════

  getAnchorPosition(system: AnatomicalSystem): THREE.Vector3 {
    const anchor = BODY_ANCHORS[system];
    if (!anchor) return new THREE.Vector3(0, 0.5, 0);
    return anchor.clone().applyMatrix4(this.matrixWorld);
  }

  highlightSystem(system: AnatomicalSystem): void {
    this.activeHighlights.add(system);
  }

  clearHighlight(system: AnatomicalSystem): void {
    this.activeHighlights.delete(system);
    const mesh = this.regionHighlights.get(system);
    if (mesh) (mesh.material as THREE.MeshBasicMaterial).opacity = 0;
  }

  clearAllHighlights(): void {
    this.activeHighlights.clear();
    this.regionHighlights.forEach(m => {
      (m.material as THREE.MeshBasicMaterial).opacity = 0;
    });
  }

  updateConnections(nodePositions: Array<{ system: AnatomicalSystem; position: THREE.Vector3 }>): void {
    while (this.connectionLines.children.length > 0) {
      const c = this.connectionLines.children[0];
      if (c instanceof THREE.Line) {
        c.geometry.dispose();
        (c.material as THREE.Material).dispose();
      }
      this.connectionLines.remove(c);
    }

    nodePositions.forEach(({ system, position }) => {
      const anchor = this.getAnchorPosition(system);
      const color = SYSTEM_COLORS[system] || 0x2997FF;

      // Línea curva con punto medio elevado
      const mid = anchor.clone().lerp(position, 0.5);
      mid.y += 0.3;
      const curve = new THREE.QuadraticBezierCurve3(anchor, mid, position);
      const pts = curve.getPoints(16);

      const geo = new THREE.BufferGeometry().setFromPoints(pts);
      const mat = new THREE.LineDashedMaterial({
        color, transparent: true, opacity: 0.35,
        dashSize: 0.15, gapSize: 0.08,
      });
      const line = new THREE.Line(geo, mat);
      line.computeLineDistances();
      this.connectionLines.add(line);
    });
  }

  // ═══════════════════════════════════════════════════════════════════════
  // UPDATE
  // ═══════════════════════════════════════════════════════════════════════

  update(deltaTime: number): void {
    this.pulsePhase += deltaTime * 2;

    // Highlights activos
    this.activeHighlights.forEach(system => {
      const mesh = this.regionHighlights.get(system);
      if (mesh) {
        const mat = mesh.material as THREE.MeshBasicMaterial;
        mat.opacity = 0.2 + Math.sin(this.pulsePhase * 3) * 0.12;
      }
      const sprite = this.anchorSprites.get(system);
      if (sprite) {
        sprite.material.opacity = 0.8 + Math.sin(this.pulsePhase * 4) * 0.2;
        const s = 0.14 + Math.sin(this.pulsePhase * 3) * 0.03;
        sprite.scale.set(s, s, 1);
      }
    });

    // Rotación: solo cuando el usuario lo mueve
    this.rotation.y = this.userRotY;
    this.rotation.x = this.userRotX;
  }

  // ═══════════════════════════════════════════════════════════════════════
  // LIMPIEZA
  // ═══════════════════════════════════════════════════════════════════════

  dispose(): void {
    this.traverse(child => {
      if (child instanceof THREE.Mesh || child instanceof THREE.Line) {
        child.geometry.dispose();
        const mat = child.material;
        if (Array.isArray(mat)) mat.forEach(m => m.dispose());
        else (mat as THREE.Material).dispose();
      }
      if (child instanceof THREE.Sprite) {
        child.material.map?.dispose();
        child.material.dispose();
      }
    });
  }
}

export { BODY_ANCHORS, SYSTEM_COLORS };
export default HumanModel;
